import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import images from "../../../assets";
import { get_public_branches } from "../../../apis/Branches";
import { CATALOGUE, acronymNotes, linkFor, spellOut, yearsForStage } from "../../../constants/catalogue";

// Site header.
//
// Replaces the old one, which rendered a dead menus API plus a hardcoded row
// of links — several of which (OC Scholarship, CS History, Jobs @ CS) pointed
// at "#" and went nowhere. Every item here resolves to a real page.
//
// Deliberately plain React: the previous markup relied on Bootstrap's JS for
// dropdowns and the collapse menu, which is fragile and pulls in jQuery.

import { C, FONT } from "../../../constants/mq";

// Laid out like mq.edu.au: a dark utility strip for people who already know
// us (state, current students, contact), then a white bar with the wordmark on
// the left and the main menu in spaced capitals on the right.
const BRAND = C.red;
const NAVY = C.ink;

const COURSES = "courses";
const ABOUT = "about";
const STATE = "state";
const BASE_STATES = ["NSW", "VIC", "QLD"];

const aboutLinks = [
  { label: "About Us", to: "/about-us" },
  { label: "Our History", to: "/our-history" },
  { label: "Campuses", to: "/our-branches" },
];

const resourceLinks = [
  { label: "Resources", to: "/resources" },
  { label: "Contact Us", to: "/contact-us" },
];

const s = {
  wrap: {
    position: "sticky",
    top: 0,
    zIndex: 900,
    background: "#ffffff",
    boxShadow: "0 1px 0 rgba(0,0,0,0.06)",
    fontFamily: FONT,
  },
  // ---- utility strip ----
  util: { background: C.utility },
  utilInner: {
    maxWidth: 1072,
    margin: "0 auto",
    padding: "0 24px",
    height: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 22,
  },
  utilLink: {
    fontSize: 12.5,
    color: "#ffffff",
    textDecoration: "none",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  // ---- main bar ----
  bar: {
    maxWidth: 1072,
    margin: "0 auto",
    padding: "0 24px",
    height: 80,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  logo: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0, textDecoration: "none" },
  logoImg: { height: 40, width: "auto", display: "block" },
  wordmark: { display: "flex", flexDirection: "column", lineHeight: 1 },
  wordTop: { fontSize: 19, fontWeight: 700, color: C.ink, letterSpacing: "0.2px" },
  wordSub: { fontSize: 9.5, fontWeight: 600, color: C.muted, letterSpacing: "1.6px", marginTop: 5, textTransform: "uppercase" },
  nav: { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, flex: 1 },
  navBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    background: "none",
    border: "none",
    borderBottom: "3px solid transparent",
    padding: "28px 12px 25px",
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: "1.6px",
    textTransform: "uppercase",
    color: C.ink,
    cursor: "pointer",
    textDecoration: "none",
    whiteSpace: "nowrap",
  },
  navBtnOpen: { borderBottomColor: C.bright },
  caret: { fontSize: 8, opacity: 0.6, marginTop: 1 },
  right: { display: "flex", alignItems: "center", gap: 6, flexShrink: 0, marginLeft: 14 },
  ghost: {
    padding: "9px 14px",
    fontSize: 14,
    fontWeight: 500,
    color: C.ink,
    textDecoration: "none",
    borderRadius: 4,
    whiteSpace: "nowrap",
  },
  // ---- dropdowns ----
  panel: {
    position: "absolute",
    left: 0,
    right: 0,
    top: "100%",
    background: "#ffffff",
    borderTop: `1px solid ${C.line}`,
    boxShadow: "0 18px 40px rgba(0,0,0,0.10)",
  },
  panelInner: { maxWidth: 1072, margin: "0 auto", padding: "28px 24px 32px" },
  megaCols: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 24,
  },
  stageTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: BRAND,
    margin: "0 0 12px",
  },
  yearBlock: { marginBottom: 16 },
  yearName: { fontSize: 13, fontWeight: 700, color: NAVY, margin: "0 0 6px" },
  subjectLink: {
    display: "block",
    fontSize: 13,
    color: C.body,
    textDecoration: "none",
    padding: "3px 0",
  },
  acronymNote: { fontSize: 11.5, lineHeight: 1.45, color: C.muted, margin: "0 0 6px" },
  panelFoot: {
    marginTop: 18,
    paddingTop: 16,
    borderTop: `1px solid ${C.line}`,
    display: "flex",
    gap: 28,
    flexWrap: "wrap",
  },
  // small dropdown (About / Resources / State)
  smallPanel: {
    position: "absolute",
    top: "100%",
    minWidth: 210,
    background: "#ffffff",
    border: `1px solid ${C.line}`,
    borderRadius: 4,
    boxShadow: "0 14px 34px rgba(0,0,0,0.12)",
    padding: 8,
    zIndex: 5,
  },
  smallItem: {
    display: "block",
    padding: "9px 12px",
    fontSize: 14,
    color: C.ink,
    textDecoration: "none",
    borderRadius: 4,
    background: "none",
    border: "none",
    width: "100%",
    textAlign: "left",
    cursor: "pointer",
    letterSpacing: "normal",
    textTransform: "none",
  },
  // ---- mobile ----
  burger: {
    display: "none",
    marginLeft: "auto",
    background: "none",
    border: `1px solid ${C.line}`,
    borderRadius: 4,
    padding: "8px 12px",
    fontSize: 18,
    cursor: "pointer",
    color: NAVY,
  },
  mobilePanel: {
    borderTop: `1px solid ${C.line}`,
    padding: "12px 24px 24px",
    background: "#ffffff",
  },
  mobileGroupTitle: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "1.4px",
    textTransform: "uppercase",
    color: C.muted,
    margin: "16px 0 6px",
  },
  mobileLink: {
    display: "block",
    padding: "10px 0",
    fontSize: 15,
    color: C.ink,
    textDecoration: "none",
    borderBottom: `1px solid ${C.sand}`,
  },
};

// The header lays out on one row on desktop and collapses on narrow screens.
// Injected once rather than pulled from the legacy stylesheet so this
// component stays self-contained.
const responsiveCss = `
@media (max-width: 1080px) {
  .cs-nav-desktop { display: none !important; }
  .cs-nav-right { display: none !important; }
  .cs-util { display: none !important; }
  .cs-burger { display: inline-block !important; }
}
@media (min-width: 1081px) {
  .cs-mobile-panel { display: none !important; }
}
.cs-subject-link:hover { color: ${BRAND}; }
.cs-small-item:hover { background: ${C.sand}; }
.cs-nav-item:hover { border-bottom-color: ${C.bright} !important; }
.cs-util-link:hover { text-decoration: underline; }
`;

const Header = () => {
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [states, setStates] = useState([]);
  const [activeState, setActiveState] = useState(
    () => localStorage.getItem("cs_state") || ""
  );
  const rootRef = useRef(null);
  const location = useLocation();

  // NSW, VIC and QLD are always offered (VIC and QLD have no content yet);
  // any other state with a campus is added from the branch list.
  useEffect(() => {
    let alive = true;
    get_public_branches().then((rows) => {
      if (!alive) return;
      const found = [...new Set([...BASE_STATES, ...(rows || []).map((b) => b.state).filter(Boolean)])];
      setStates(found);
      setActiveState((cur) => cur || found[0] || "");
    });
    return () => {
      alive = false;
    };
  }, []);

  // Close everything on navigation.
  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  // Click-away and Escape.
  useEffect(() => {
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenMenu(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const toggle = (key) => setOpenMenu((cur) => (cur === key ? null : key));

  const chooseState = (st) => {
    setActiveState(st);
    localStorage.setItem("cs_state", st);
    setOpenMenu(null);
  };

  const stages = useMemo(
    () => [
      { key: "primary", title: "Primary", years: yearsForStage("primary") },
      { key: "high", title: "High School", years: yearsForStage("high") },
      { key: "senior", title: "Senior (HSC)", years: yearsForStage("senior") },
    ],
    []
  );

  const subjectHref = (year, subject) => linkFor(year, subject);

  const renderSubject = (year, subject) => {
    const target = subjectHref(year, subject.name);
    if (!target) return null;
    const label = subject.label || subject.name;
    return target.external ? (
      <a
        key={`${year}-${subject.name}`}
        className="cs-subject-link"
        style={s.subjectLink}
        href={target.href}
        target="_blank"
        rel="noopener noreferrer"
        title={spellOut(label) || undefined}
      >
        {label}
      </a>
    ) : (
      <Link
        key={`${year}-${subject.name}`}
        className="cs-subject-link"
        style={s.subjectLink}
        to={target.href}
        title={spellOut(label) || undefined}
      >
        {label}
      </Link>
    );
  };

  const navBtnStyle = (key) => ({
    ...s.navBtn,
    ...(openMenu === key ? s.navBtnOpen : null),
  });

  return (
    <header style={s.wrap} ref={rootRef}>
      <style>{responsiveCss}</style>

      {/* utility strip: for people who already belong */}
      <div className="cs-util" style={s.util}>
        <div style={s.utilInner}>
          {states.length > 0 && (
            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="cs-util-link"
                style={s.utilLink}
                onClick={() => toggle(STATE)}
                aria-expanded={openMenu === STATE}
              >
                {activeState || "State"} <span style={s.caret}>&#9660;</span>
              </button>
              {openMenu === STATE && (
                <div style={{ ...s.smallPanel, right: 0, minWidth: 140, marginTop: 8 }}>
                  {states.map((st) => (
                    <button
                      key={st}
                      type="button"
                      className="cs-small-item"
                      style={s.smallItem}
                      onClick={() => chooseState(st)}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          {/* Named for the person, not the mechanism — someone scanning a
              header is looking for themselves. */}
          <Link className="cs-util-link" style={s.utilLink} to="/current-students">
            Current students
          </Link>
          <Link className="cs-util-link" style={s.utilLink} to="/events">
            Events
          </Link>
          <Link className="cs-util-link" style={s.utilLink} to="/resources">
            Resources
          </Link>
          <Link className="cs-util-link" style={s.utilLink} to="/contact-us">
            Contact
          </Link>
        </div>
      </div>

      <div style={{ position: "relative" }}>
        <div style={s.bar}>
          <Link to="/" style={s.logo}>
            <img {...images["cs-logo"]} alt="" style={s.logoImg} />
            <span style={s.wordmark}>
              <span style={s.wordTop}>CS Education</span>
            </span>
          </Link>

          <nav className="cs-nav-desktop" style={s.nav}>
            <button
              type="button"
              className="cs-nav-item"
              style={navBtnStyle(COURSES)}
              onClick={() => toggle(COURSES)}
              aria-expanded={openMenu === COURSES}
            >
              Courses <span style={s.caret}>&#9660;</span>
            </button>

            <div style={{ position: "relative" }}>
              <button
                type="button"
                className="cs-nav-item"
                style={navBtnStyle(ABOUT)}
                onClick={() => toggle(ABOUT)}
                aria-expanded={openMenu === ABOUT}
              >
                CS Education <span style={s.caret}>&#9660;</span>
              </button>
              {openMenu === ABOUT && (
                <div style={s.smallPanel}>
                  {aboutLinks.map((l) => (
                    <Link
                      key={l.to}
                      className="cs-small-item"
                      style={s.smallItem}
                      to={l.to}
                    >
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link className="cs-nav-item" style={s.navBtn} to="/why-cs">
              Why CS
            </Link>
            <Link className="cs-nav-item" style={s.navBtn} to="/pricing">
              Pricing
            </Link>

          </nav>

          <div className="cs-nav-right" style={s.right}>
            <Link className="mq-btn mq-btn--sm" to="/enroll">
              Enrol now
            </Link>
          </div>

          <button
            type="button"
            className="cs-burger"
            style={s.burger}
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            &#9776;
          </button>
        </div>

        {openMenu === COURSES && (
          <div style={s.panel}>
            <div style={s.panelInner}>
              <div style={s.megaCols}>
                {stages.map((stage) => (
                  <div key={stage.key}>
                    <p style={s.stageTitle}>{stage.title}</p>
                    {stage.years.map((y) => (
                      <div key={y.year} style={s.yearBlock}>
                        <p style={s.yearName}>Year {y.year}</p>
                        {y.subjects.map((sub) => renderSubject(y.year, sub))}
                      </div>
                    ))}
                    {acronymNotes(
                      stage.years.flatMap((y) => y.subjects.map((sub) => sub.label || sub.name))
                    ).map((n) => (
                      <p key={n} style={s.acronymNote}>{n}</p>
                    ))}
                  </div>
                ))}
              </div>
              <div style={s.panelFoot}>
                <Link className="mq-link" to="/our-courses">
                  See all courses
                </Link>
                <Link className="mq-link" to="/enroll">
                  How to enrol
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {mobileOpen && (
        <div className="cs-mobile-panel" style={s.mobilePanel}>
          <p style={s.mobileGroupTitle}>Courses</p>
          {CATALOGUE.map((y) => (
            <Link key={y.year} style={s.mobileLink} to="/our-courses">
              Year {y.year}
            </Link>
          ))}

          <p style={s.mobileGroupTitle}>CS Education</p>
          {aboutLinks.map((l) => (
            <Link key={l.to} style={s.mobileLink} to={l.to}>
              {l.label}
            </Link>
          ))}

          <p style={s.mobileGroupTitle}>More</p>
          <Link style={s.mobileLink} to="/why-cs">
            Why CS
          </Link>
          <Link style={s.mobileLink} to="/pricing">
            Pricing
          </Link>
          <Link style={s.mobileLink} to="/events">
            Events
          </Link>
          {resourceLinks.map((l) => (
            <Link key={l.to} style={s.mobileLink} to={l.to}>
              {l.label}
            </Link>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <Link
              style={{ ...s.ghost, border: `1px solid ${C.line}` }}
              to="/current-students"
            >
              Current students
            </Link>
            <Link className="mq-btn mq-btn--sm" to="/enroll">
              Enrol now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
